/**
 * ScheduleV2 — drag-drop preview of the schedule.
 *
 * Layout:
 *   ┌────────────┬─────────────────────────────────────────┐
 *   │            │  Header: toggle, nav, view switcher      │
 *   │ Unscheduled├─────────────────────────────────────────┤
 *   │   tasks    │                                          │
 *   │  sidebar   │   Month | Week | Day | Agenda            │
 *   │  (dnd src) │   (dnd targets — phase by phase)         │
 *   │            │                                          │
 *   └────────────┴─────────────────────────────────────────┘
 *
 * Data-wise V2 is identical to V1 — same /api/schedule/events and
 * /api/schedule/entries endpoints, same /api/hierarchy/tasks for the
 * unscheduled sidebar. Flipping Classic ↔ New is lossless.
 *
 * V2 intentionally does NOT reimplement sync UI, recurring-master edit
 * dialogs, push/pull status pills, etc. Users who need those flip back to
 * Classic via the header toggle. That keeps this preview focused on the
 * drag-drop story without dragging all of Schedule.tsx's complexity with it.
 *
 * Phase status:
 *   - Phase 1 (this commit): month-view day-level drop; sidebar in place.
 *   - Phase 2: time-slot drop zones in week + day.
 *   - Phase 3: existing events become draggable for reschedule.
 *   - Phase 4: per-day opportunistic lane with location pills.
 */

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import {
  addDays,
  addMonths,
  addWeeks,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  parseISO,
  setHours,
  setMinutes,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
  subWeeks,
} from 'date-fns'
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Clock,
  Inbox,
  Loader2,
  Sparkles,
  X,
} from 'lucide-react'

import { Button } from '../components/ui/Button'
import { ConfirmationModal } from '../components/ui/ConfirmationModal'
import { useToast } from '../components/ui/Toast'
import { useServerTime } from '../hooks/useServerTime'
import { useNow } from '../hooks/useNow'
import { UnscheduledTasksSidebar } from '../components/schedule/UnscheduledTasksSidebar'
import {
  DraggableTaskCard,
  TASK_DRAG_TYPE,
  type TaskDragData,
} from '../components/schedule/DraggableTaskCard'
import {
  DayDropCell,
  DAY_DROP_TYPE,
  type DayDropData,
} from '../components/schedule/DayDropCell'
import {
  TimeSlotDropZone,
  TIME_SLOT_DROP_TYPE,
  type TimeSlotDropData,
  minutesToTimeString,
} from '../components/schedule/TimeSlotDropZone'
import {
  DraggableEventBlock,
  EVENT_DRAG_TYPE,
  type ExistingEventDragData,
  type ExistingEventPayload,
} from '../components/schedule/DraggableEventBlock'
import { buildEventBoxes } from '../components/schedule/layoutDayEvents'
import {
  EventEditModal,
  type EventEditInitial,
  type EventEditValues,
  type SaveScope,
} from '../components/schedule/EventEditModal'
import {
  OpportunisticLane,
  LocationLaneBadge,
  type OpportunisticLaneTask,
} from '../components/schedule/OpportunisticLane'
import {
  LOCATION_PILL_DRAG_TYPE,
  type LocationPillDragData,
} from '../components/schedule/LocationPill'
import {
  UnscheduleBuffer,
  BUFFER_DROP_TYPE,
  type BufferDropData,
} from '../components/schedule/UnscheduleBuffer'
import { PastTimeOverlay } from '../components/schedule/PastTimeOverlay'
import {
  CommitmentChip,
  CommitmentGlyph,
} from '../components/schedule/CommitmentBadge'
import {
  COMMITMENT_HINT,
  commitmentBorder,
  commitmentFillAlpha,
} from '../components/schedule/commitmentPresentation'
import { PRODUCT_NAME } from '../components/schedule/productName'
import { removeBlockFromDay } from '../services/aiSchedulerService'
import { placeService, type Place } from '../services/placeService'
import { TaskService, type Task } from '../services/taskService'
import {
  createOccurrenceException,
  fetchBlockCommitment,
  readCommitment,
  updateHabitDefinition,
  updateScheduleEvent,
  type CommitmentState,
  type MutationOutcome,
} from '../services/scheduleService'

// ───────── types ─────────

type ViewType = 'month' | 'week' | 'day' | 'agenda'

interface ScheduleEvent {
  id: string
  title: string
  description?: string
  start: Date
  end?: Date
  allDay?: boolean
  color: string
  type: 'task' | 'action_item' | 'event' | 'meeting' | 'google_calendar' | 'schedule'
  status?: string
  /** For schedule entries from yanamas' DB, the underlying item_id */
  item_id?: number | string
  source?: 'horton' | 'google_calendar'
  /** Set when this event was created from a task — lets us identify "already scheduled". */
  parent_task_id?: number
  parent_action_item_id?: number
  is_virtual?: boolean
  is_recurring_instance?: boolean
  // ── Recurrence metadata ───────────────────────────────────────────────
  // These let the edit flow decide between "this occurrence only" vs
  // "all occurrences". Populated by the mapper when the schedule API
  // returns them (see `entries.forEach` below). Anything missing falls
  // back to "non-recurring" handling, which is always safe.
  is_recurring_master?: boolean
  /** When this event is an instance/exception, id of the master entry. */
  master_schedule_id?: number
  /** Habit definition id for habit occurrences. */
  recurring_source_id?: number | string
  /** "habit_occurrence" | "task" | etc. From the schedule store. */
  task_kind?: string
  /** YYYY-MM-DD of the occurrence's original date (needed for exceptions). */
  original_date?: string
  // ── Calendar commitment (constitution §2 dim-4 / §3) ─────────────────
  /**
   * 'flexible' (Horton's plan, still movable) or 'confirmed' (the user's
   * plan). `null`/undefined means the payload said nothing — the block
   * then shows no lock affordance at all. Never inferred here: the
   * backend resolves it and we render what it says.
   */
  commitment?: CommitmentState | null
}

interface PendingDrop {
  /** The optimistic ScheduleEvent.id we inserted synchronously on drop. */
  tempId: string
  /** Undo the optimistic insert. Invoked on Cancel or on POST failure. */
  rollback: () => void
  /** Task being scheduled (title, duration, ids). */
  task: Task
  /** YYYY-MM-DD of the target day. */
  dateIso: string
  /** HH:MM start time. Always present — for day-level drops we resolve it
   *  via defaultStartForDay(target, now) *before* showing the modal so the
   *  user sees exactly what they're confirming. */
  startTime: string
  /** Duration in minutes. */
  duration: number
  /** True when the drop landed on a specific time slot (shows time in the
   *  modal); false for day-level drops (shows "around {startTime}"). */
  wasTimeSlot: boolean
  /** Date object for the target start — precomputed for nicer formatting. */
  targetDate: Date
}

// Color tokens intentionally mirror V1 so the comparison is fair.
const EVENT_COLORS = {
  task: 'var(--semantic-success)',
  action_item: 'var(--semantic-warning)',
  event: 'var(--brand-primary)',
  meeting: 'var(--accent-gold)',
  google_calendar: 'var(--vendor-google)',
  habit: 'var(--brand-light)',
  default: 'var(--text-tertiary)',
}

const HOURS = Array.from({ length: 24 }, (_, i) => i)
const HOUR_HEIGHT = 64
const WEEK_HOUR_HEIGHT = 48

/**
 * Project a ScheduleEvent into the ExistingEventPayload the pill needs.
 * Centralised so the week-view and day-view renderers can't drift — they
 * both need the recurring metadata for the edit modal. `durationMin` is
 * computed from start/end with a 15-minute floor so freshly dropped
 * pills don't collapse to zero height while the server write is in
 * flight.
 */
function buildPillPayload(
  ev: ScheduleEvent,
): import('../components/schedule/DraggableEventBlock').ExistingEventPayload {
  const startMin = ev.start.getHours() * 60 + ev.start.getMinutes()
  const endMin =
    ev.end != null
      ? ev.end.getHours() * 60 + ev.end.getMinutes()
      : startMin + 30
  // Virtual recurring instances have `item_id` unset (their React id is
  // the only stable handle). Fall back to the master_schedule_id so
  // drag + click handlers still have a number to route on — the save
  // dispatcher later decides master vs instance semantics.
  const effectiveItemId =
    typeof ev.item_id === 'number'
      ? ev.item_id
      : typeof ev.master_schedule_id === 'number'
      ? ev.master_schedule_id
      : Number.NaN
  return {
    id: ev.id,
    itemId: effectiveItemId,
    title: ev.title,
    description: ev.description,
    start: ev.start,
    end: ev.end,
    color: ev.color || EVENT_COLORS.default,
    durationMin: Math.max(endMin - startMin, 15),
    isRecurringInstance: !!ev.is_recurring_instance,
    isRecurringMaster: !!ev.is_recurring_master,
    masterScheduleId:
      typeof ev.master_schedule_id === 'number' ? ev.master_schedule_id : undefined,
    recurringSourceId: ev.recurring_source_id,
    taskKind: ev.task_kind,
    originalDate: ev.original_date,
    commitment: ev.commitment ?? null,
  }
}

function toDisplayString(value: unknown, fallback = ''): string {
  if (value == null || value === '') return fallback
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  try {
    return JSON.stringify(value)
  } catch {
    return fallback
  }
}

/**
 * Pick the hour we should scroll the time-grid to when a view first
 * renders (or when currentDate/events change).
 *
 * Preference order:
 *   1. 1 hour before the earliest-starting event in the visible range
 *      (clamped to [0, 22]) — so the user lands in the thick of their
 *      day.
 *   2. 8 AM — the start of the default working-hours band (8–20).
 *
 * Returns an integer hour in [0, 23].
 */
function pickScrollHour(events: ScheduleEvent[]): number {
  let earliest: number | null = null
  for (const e of events) {
    if (e.allDay) continue
    const h = e.start.getHours()
    if (earliest == null || h < earliest) earliest = h
  }
  if (earliest != null) return Math.max(0, Math.min(22, earliest - 1))
  return 8
}

/** Round a Date forward to the next 15-minute multiple → "HH:MM". */
function ceilTo15(d: Date): string {
  const m = d.getMinutes()
  const add = (15 - (m % 15)) % 15
  const total = d.getHours() * 60 + m + add
  const hh = Math.floor(total / 60) % 24
  const mm = total % 60
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`
}

/** Default start time when dropping on a day cell (no specific time slot). */
function defaultStartForDay(target: Date, now: Date): string {
  return isSameDay(target, now) ? ceilTo15(now) : '09:00'
}

// ───────── data fetching ─────────

/**
 * The `?status=` value that turns `/api/schedule/entries` from a display
 * reader into the HOLD read model.
 *
 * Under HOLD-first (ratified decision #1) yanamas-placed work lands as
 * `block_status='proposed'`, and the entries reader hides those rows by
 * default — `virtual_recurrence_service.get_schedule_events_for_range`
 * strips them before any caller-supplied filter runs. Without this the
 * flagship calendar renders an empty day while yanamas holds a full plan
 * for it: the Flexible/Confirmed distinction SC-14 gates the whole flip
 * on cannot appear, because the Flexible rows are not in the payload.
 */
const HOLD_READ_STATUS = 'proposed'

/**
 * What a 202 `applied: false` says out loud.
 *
 * The server accepted the change and has not applied it. Reporting that as
 * success would tell the user their calendar moved when it did not, so
 * every verb on this page that can receive a 202 says this instead.
 */
const PENDING_CHANGE_COPY = `${PRODUCT_NAME} has it and hasn't applied it yet.`

/**
 * Why the holds arrive in a SECOND request instead of widening the first
 * one to `?status=proposed,confirmed`.
 *
 * `schedule_controller._keep_by_status` is an ALLOW-LIST over
 * `scheduling_policy.resolved_block_status`, so any `?status=` value
 * drops every row whose resolved status is not named — including rows
 * that have no `block_status` at all. Those exist and are live:
 * `admission/mutation.py::schedule_block_for_item` writes auto-placed
 * blocks through `ScheduleRepository.create_schedule`, which stamps no
 * `block_status` (deliberately — see that function's docstring), and the
 * default reader keeps them because its exclusion is a `$nin`, which
 * matches absent fields. A single `?status=proposed,confirmed` read would
 * therefore trade "holds are invisible" for "every admission-placed block
 * is invisible" — the same bug pointed the other way.
 *
 * So: request 1 is byte-for-byte the display read this page has always
 * done, and request 2 asks only for the holds it hides. Nothing that
 * renders today can stop rendering, and a failing hold read degrades to
 * exactly the old behaviour.
 */
function scheduleEntriesUrl(
  startStr: string,
  endStr: string,
  status?: string,
): string {
  const base = `/api/schedule/entries?start=${startStr}&end=${endStr}`
  return status ? `${base}&status=${encodeURIComponent(status)}` : base
}

/**
 * The part of an `/api/schedule/entries` row this page reads.
 *
 * Every field is optional because the payload is one shape for real rows,
 * recurring masters, virtual expansions and exceptions, and each of those
 * carries a different subset. Written down rather than left as `any` so a
 * key the backend renames is a compile error here instead of a silently
 * undefined pill — which is how the commitment pair went missing in the
 * first place.
 */
interface ScheduleEntryPayload {
  id: number | string
  date?: string
  /** "HH:MM-HH:MM"; the end half is optional. */
  time_slot?: string
  duration?: number
  activity?: unknown
  title?: unknown
  description?: unknown
  status?: string
  task_kind?: string
  parent_task_id?: number
  parent_action_item_id?: number
  is_virtual?: boolean
  is_recurring_instance?: boolean
  is_recurring_master?: boolean
  master_schedule_id?: number
  master_id?: number
  recurring_source_id?: number | string
  original_date?: string
  /** §3 pair, when the payload projects it. Read via `readCommitment`. */
  commitment?: unknown
  commitment_state?: unknown
  commitment_source?: unknown
}

/**
 * Map one `/api/schedule/entries` row into the calendar's event shape.
 *
 * Shared by the display read and the hold read so the two can never
 * drift into rendering the same row two different ways.
 *
 * `fromHoldRead` affects exactly one field — see the `commitment`
 * comment below — and nothing about placement, colour or geometry.
 */
function mapScheduleEntry(
  s: ScheduleEntryPayload,
  fallbackDate: Date,
  fromHoldRead: boolean,
): ScheduleEvent {
  let eventDate: Date
  if (s.date) {
    const [y, m, d] = s.date.split('-').map(Number)
    eventDate = new Date(y, m - 1, d)
  } else {
    eventDate = fallbackDate
  }
  let start = eventDate
  let end = eventDate
  if (s.time_slot) {
    const [startPart, endPart] = s.time_slot.split('-')
    const [sh, sm] = startPart.trim().split(':').map(Number)
    start = setMinutes(setHours(eventDate, sh || 9), sm || 0)
    if (endPart) {
      const [eh, em] = endPart.trim().split(':').map(Number)
      end = setMinutes(setHours(eventDate, eh), em || 0)
    } else if (s.duration) {
      end = new Date(start.getTime() + s.duration * 60_000)
    } else {
      end = new Date(start.getTime() + 30 * 60_000)
    }
  } else if (s.duration) {
    end = new Date(start.getTime() + s.duration * 60_000)
  }

  let color = EVENT_COLORS.event
  if (s.task_kind === 'habit_occurrence') color = EVENT_COLORS.habit
  else if (s.is_recurring_instance) color = 'var(--brand-primary)'
  else if (s.parent_action_item_id) color = EVENT_COLORS.action_item
  else if (s.parent_task_id) color = EVENT_COLORS.task

  return {
    id: `schedule-${s.id}`,
    title: toDisplayString(s.activity ?? s.title, 'Schedule item'),
    description: toDisplayString(s.description) || undefined,
    start,
    end,
    allDay: false,
    color,
    type: 'schedule',
    status: s.status,
    item_id: typeof s.id === 'string' && s.id.includes('_v_') ? undefined : s.id,
    source: 'horton',
    parent_task_id: s.parent_task_id,
    parent_action_item_id: s.parent_action_item_id,
    is_virtual: !!s.is_virtual,
    is_recurring_instance: !!(s.is_recurring_instance || s.is_virtual),
    is_recurring_master: !!s.is_recurring_master,
    master_schedule_id:
      typeof s.master_schedule_id === 'number' ? s.master_schedule_id : s.master_id,
    recurring_source_id: s.recurring_source_id,
    task_kind: s.task_kind,
    original_date: s.original_date,
    // Whatever the row says about its own commitment, verbatim. Rows that
    // carry nothing stay null and render no chip.
    //
    // The one exception is a row from the HOLD read, and it is not an
    // inference from the row's fields — it is the request we made. That
    // read returns `block_status='proposed'` rows and nothing else
    // (`_resolve_status_filter` strips terminal statuses server-side), and
    // `scheduling_policy.resolved_commitment` resolves exactly that shape
    // to ('flexible', 'ai_proposal') at ladder rule (c). Saying "Flexible"
    // here is agreeing with the server's own resolver about a row we asked
    // it for by that name — not treating silence as a commitment, which is
    // the failure mode SC-35 forbids and which stays forbidden below.
    //
    // It is also a backstop, not the mechanism. `/api/schedule/entries`
    // projects the §3 pair (`virtual_recurrence_service._schedule_to_dict`
    // resolves it for real and virtual rows alike), so the `??` is
    // unreached against a current backend — a projected value always wins.
    // It stays for the deploy skew where the two ends disagree, which is
    // the whole reason this bug was invisible the first time.
    commitment: readCommitment(s).state ?? (fromHoldRead ? 'flexible' : null),
  }
}

interface ScheduleData {
  events: ScheduleEvent[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  /** Local mutation for optimistic UI. Returns a rollback function. */
  mutate: (fn: (prev: ScheduleEvent[]) => ScheduleEvent[]) => () => void
}

function useScheduleData(currentDate: Date | null, viewType: ViewType): ScheduleData {
  const [events, setEvents] = useState<ScheduleEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    if (!currentDate) return
    setLoading(true)
    setError(null)
    try {
      // Mirror V1's window logic so the event set is identical.
      let rangeStart: Date
      let rangeEnd: Date
      if (viewType === 'month') {
        rangeStart = startOfMonth(subMonths(currentDate, 2))
        rangeEnd = endOfMonth(addMonths(currentDate, 2))
      } else if (viewType === 'week') {
        rangeStart = startOfWeek(subWeeks(currentDate, 4))
        rangeEnd = endOfWeek(addWeeks(currentDate, 4))
      } else {
        rangeStart = addDays(currentDate, -14)
        rangeEnd = addDays(currentDate, 14)
      }

      const startStr = format(rangeStart, 'yyyy-MM-dd')
      const endStr = format(rangeEnd, 'yyyy-MM-dd')

      const [eventsRes, scheduleRes, holdRes] = await Promise.all([
        fetch('/api/schedule/events?include_google=true', { credentials: 'include' }),
        fetch(scheduleEntriesUrl(startStr, endStr), { credentials: 'include' }),
        // The hold read (see scheduleEntriesUrl). Additive and optional:
        // it never removes a row the display read returned, and its
        // failure is swallowed here rather than failing the whole load —
        // a calendar missing yanamas' proposals is degraded; a calendar
        // that refuses to render is broken.
        fetch(scheduleEntriesUrl(startStr, endStr, HOLD_READ_STATUS), {
          credentials: 'include',
        }).catch(() => null),
      ])

      const all: ScheduleEvent[] = []

      if (eventsRes.ok) {
        const data = await eventsRes.json()
        if (data.success && data.events) {
          data.events.forEach((e: any) => {
            if (e.source === 'google_calendar' || e.type === 'google_calendar') {
              const start = parseISO(e.start)
              const end = e.end ? parseISO(e.end) : start
              all.push({
                id: e.id,
                title: toDisplayString(e.title, 'Event'),
                description: toDisplayString(e.description) || undefined,
                start,
                end,
                allDay: !!e.is_all_day,
                color: e.color || EVENT_COLORS.google_calendar,
                type: 'google_calendar',
                status: e.status,
                item_id: e.item_id,
                source: 'google_calendar',
              })
            }
          })
        }
      }

      const entryRows = (data: unknown): ScheduleEntryPayload[] => {
        if (Array.isArray(data)) return data as ScheduleEntryPayload[]
        if (!data || typeof data !== 'object') return []
        const body = data as { success?: unknown; entries?: unknown }
        if (body.success && Array.isArray(body.entries)) {
          return body.entries as ScheduleEntryPayload[]
        }
        return []
      }

      if (scheduleRes.ok) {
        // No `.catch` on this parse, deliberately. A dev proxy with no
        // /api/schedule route answers 200 with index.html, and letting
        // that throw is what puts the error banner on screen instead of
        // an empty, confident-looking calendar.
        for (const s of entryRows(await scheduleRes.json())) {
          all.push(mapScheduleEntry(s, currentDate, false))
        }
      }

      // Holds last, and only the ones the display read did not already
      // return. The two reads are disjoint by construction (the display
      // read excludes `proposed`), but a row arriving twice would draw two
      // pills on the same slot, so the guard is on the id rather than on
      // that assumption holding forever.
      if (holdRes && holdRes.ok) {
        const seenIds = new Set(all.map((e) => e.id))
        for (const s of entryRows(await holdRes.json().catch(() => null))) {
          const hold = mapScheduleEntry(s, currentDate, true)
          if (seenIds.has(hold.id)) continue
          seenIds.add(hold.id)
          all.push(hold)
        }
      }

      // Merge server response with any optimistic pills the parent hasn't
      // yet had the server confirm. Without this, switching viewType (or
      // any other refetch trigger) would wipe a freshly-placed task pill
      // whenever the server's write hasn't propagated into the read path —
      // the symptom the user saw on the Agenda tab after dropping a task.
      //
      // Contract: an "optimistic" pill is one whose React `id` still starts
      // with "optimistic-" (ScheduleV2 stamps those on drop).
      //
      // Two cases we must preserve:
      //   1. POST is still inflight → pill has NO `item_id` yet. The
      //      confirmation modal closes synchronously on click (pendingDrop
      //      nulled before `await fetch`), so the user can navigate during
      //      the POST window and trigger a refetch. Dropping the pill here
      //      makes handleConfirmDrop's post-POST `mutate` a no-op and the
      //      event visually vanishes.
      //   2. POST succeeded, pill has `item_id`, but the server's read
      //      path hasn't picked up the new row yet (virtual-recurrence
      //      expansion lag).
      //
      // We drop the pill only when the server's response DOES include its
      // `item_id` — that's the clean convergence point. Until then, keep
      // showing it.
      setEvents((prev) => {
        const serverItemIds = new Set<number>()
        for (const e of all) {
          if (typeof e.item_id === 'number') serverItemIds.add(e.item_id)
        }
        const preserved = prev.filter(
          (e) =>
            typeof e.id === 'string' &&
            e.id.startsWith('optimistic-') &&
            !(typeof e.item_id === 'number' && serverItemIds.has(e.item_id)),
        )
        return preserved.length > 0 ? [...all, ...preserved] : all
      })
    } catch (err) {
      console.error('[schedulev2] fetch error', err)
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }, [currentDate, viewType])

  useEffect(() => {
    void fetchData()
  }, [fetchData])

  const mutate = useCallback(
    (fn: (prev: ScheduleEvent[]) => ScheduleEvent[]) => {
      let previous: ScheduleEvent[] = []
      setEvents((prev) => {
        previous = prev
        return fn(prev)
      })
      return () => setEvents(previous)
    },
    [],
  )

  return { events, loading, error, refetch: fetchData, mutate }
}

// Unscheduled = not terminal AND no existing schedule event with parent_task_id === this task id.
function deriveUnscheduled(tasks: Task[], events: ScheduleEvent[]): Task[] {
  const scheduledTaskIds = new Set<number>()
  for (const e of events) {
    if (e.parent_task_id != null) scheduledTaskIds.add(Number(e.parent_task_id))
  }
  return tasks.filter((t) => {
    if (t.status === 'completed' || t.status === 'cancelled') return false
    if ((t as any).archived) return false
    return !scheduledTaskIds.has(t.id)
  })
}

// ───────── page ─────────

export const ScheduleV2: React.FC = () => {
  const navigate = useNavigate()
  const toast = useToast()
  const { serverDate, isLoading: serverTimeLoading } = useServerTime()
  // Minute-ticking clock anchored on server time. Drives the past-time
  // overlay, the "now line", and the smart-default time when dropping on
  // a day cell.
  const now = useNow(serverDate || new Date(), 60_000)

  const [currentDate, setCurrentDate] = useState<Date | null>(null)
  const [viewType, setViewType] = useState<ViewType>('month')
  // Unscheduled-tasks sidebar is a persistent 288px column on lg+ and an
  // overlay drawer on narrow viewports. Default to open on desktop, closed
  // on mobile so the calendar actually has room to render.
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() =>
    typeof window !== 'undefined'
      ? !window.matchMedia('(min-width: 1024px)').matches
      : true,
  )
  // "Plan it → Pick a time" lands here with the task to place; open the
  // unscheduled rail and highlight that card so there is something to drag.
  const location = useLocation()
  const focusTaskId = (location.state as { focusTaskId?: number } | null)?.focusTaskId ?? null
  useEffect(() => {
    if (focusTaskId != null) setSidebarCollapsed(false)
  }, [focusTaskId])
  const [tasks, setTasks] = useState<Task[]>([])
  const [places, setPlaces] = useState<Place[]>([])
  const [tasksLoading, setTasksLoading] = useState(true)
  const [activeDragTask, setActiveDragTask] = useState<Task | null>(null)
  const [activeDragEvent, setActiveDragEvent] = useState<ExistingEventPayload | null>(null)
  const [hoveredDayIso, setHoveredDayIso] = useState<string | null>(null)
  // itemId → epoch ms when first scheduled. Drives the always-visible
  // X-button grace window on freshly-placed pills.
  const [recentlyCreated, setRecentlyCreated] = useState<Record<number, number>>({})
  // Holds the state needed to either COMMIT (POST to /api/schedule/events)
  // or CANCEL (rollback the optimistic pill) a fresh drop while the user
  // looks at a confirmation dialog. The optimistic pill is already placed
  // when this is set — the dialog lets the user approve or undo.
  const [pendingDrop, setPendingDrop] = useState<PendingDrop | null>(null)
  // Ref mirrors `pendingDrop` so confirm/cancel handlers can read its
  // current value synchronously within a single click cycle (React state
  // via `useState` isn't readable until re-render). Without this,
  // ConfirmationModal's handleConfirm → onConfirm → onClose sequence
  // would read stale `pendingDrop` in both handlers and roll back the
  // drop we just confirmed. Setting the ref to null inside one handler
  // no-ops the other.
  const pendingDropRef = useRef<PendingDrop | null>(null)

  // Dwell timer used both for "month-view day → open day" mid-drag and for
  // "buffer zoom-out region" dwell-trigger. Single ref because at most one
  // dwell countdown is active at a time.
  const dwellTimerRef = useRef<number | null>(null)
  const dwellKeyRef = useRef<string | null>(null)

  const isAnyDragging = activeDragTask != null || activeDragEvent != null

  // Helper: decide if an event is reschedulable via drag-drop.
  // Google Calendar events and anything without a usable id (neither
  // item_id nor master_schedule_id) stay fixed. Recurring instances
  // and habit occurrences ARE draggable now: the drop opens the edit
  // modal, which routes the save to the correct endpoint based on
  // whether the user chose "this occurrence" or "all occurrences".
  const isEventDraggable = (e: ScheduleEvent): boolean => {
    if (e.source === 'google_calendar') return false
    if (typeof e.item_id !== 'number' && typeof e.master_schedule_id !== 'number') {
      return false
    }
    return true
  }

  const taskServiceRef = useRef(new TaskService())

  useEffect(() => {
    if (!serverTimeLoading && serverDate && !currentDate) {
      setCurrentDate(serverDate)
    }
  }, [serverTimeLoading, serverDate, currentDate])

  // Clean up the dwell timer on unmount.
  useEffect(() => {
    return () => {
      if (dwellTimerRef.current != null) {
        window.clearTimeout(dwellTimerRef.current)
        dwellTimerRef.current = null
      }
    }
  }, [])

  // Periodically prune expired "recently created" markers so the X stops
  // being always-visible after the 12 s grace window.
  useEffect(() => {
    if (Object.keys(recentlyCreated).length === 0) return
    const id = window.setInterval(() => {
      const cutoff = Date.now() - 12_000
      setRecentlyCreated((m) => {
        let changed = false
        const next: Record<number, number> = {}
        for (const [k, v] of Object.entries(m)) {
          if (v >= cutoff) next[Number(k)] = v
          else changed = true
        }
        return changed ? next : m
      })
    }, 5_000)
    return () => window.clearInterval(id)
  }, [recentlyCreated])

  const { events, loading: eventsLoading, refetch, mutate } = useScheduleData(
    currentDate,
    viewType,
  )

  // Counter of in-flight optimistic mutations — drives a subtle "Saving…"
  // chip in the header so the user knows something is happening even when
  // the optimistic update already moved the pill/block to its new slot.
  const [pendingMutations, setPendingMutations] = useState(0)

  const loadTasks = useCallback(async () => {
    setTasksLoading(true)
    try {
      const list = await taskServiceRef.current.getTasks()
      setTasks(list)
    } catch (err) {
      console.error('[schedulev2] task load failed', err)
    } finally {
      setTasksLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadTasks()
  }, [loadTasks])

  // Places are only needed so the opportunistic lane can show each pill's
  // place title in its tooltip. Not critical — the lane still works if the
  // fetch fails, it just shows the task title without the "@ place" suffix.
  useEffect(() => {
    let cancelled = false
    void placeService
      .list()
      .then((list) => {
        if (!cancelled) setPlaces(list)
      })
      .catch(() => {
        /* non-fatal */
      })
    return () => {
      cancelled = true
    }
  }, [])

  const unscheduledTasks = useMemo(
    () => deriveUnscheduled(tasks, events),
    [tasks, events],
  )

  // Location-tied tasks — surfaced opportunistically. Rule (per plan):
  // include tasks with `place_id` AND a due date that falls within the
  // current view's lookahead window (14 days from `currentDate`). Tasks
  // without a due date are deliberately NOT surfaced here — otherwise they
  // would flood every day with a pin-badge and hide the signal. They still
  // fire via geofence regardless; this lane is just the "coming up" view.
  const placeTitleById = useMemo(() => {
    const map: Record<number, string> = {}
    for (const p of places) map[p.id] = p.title
    return map
  }, [places])

  const locationTasks = useMemo<OpportunisticLaneTask[]>(() => {
    if (!currentDate) return []
    const windowStart = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDate.getDate(),
    ).getTime()
    const windowEnd = windowStart + 14 * 24 * 3600 * 1000
    const result: OpportunisticLaneTask[] = []
    for (const t of tasks) {
      if (t.place_id == null) continue
      if (t.status === 'completed' || t.status === 'cancelled') continue
      if (!t.due_date) continue
      const due = Date.parse(t.due_date)
      if (!Number.isFinite(due)) continue
      if (due < windowStart - 24 * 3600 * 1000 || due > windowEnd) continue
      result.push({ task: t, placeTitle: placeTitleById[t.place_id] ?? null })
    }
    return result
  }, [tasks, currentDate, placeTitleById])

  const locationCountForDay = useCallback(
    (day: Date): number => {
      // Only count tasks whose due date lands on this specific day. If we
      // counted "no due date" tasks here, every day would light up with a
      // pin and the badge would carry no information.
      let count = 0
      for (const { task } of locationTasks) {
        if (!task.due_date) continue
        const d = new Date(task.due_date)
        if (isSameDay(d, day)) count += 1
      }
      return count
    },
    [locationTasks],
  )

  // ─── date + view helpers ───
  const goToToday = () => {
    if (serverDate) setCurrentDate(serverDate)
  }

  const nav = (dir: 'prev' | 'next') => {
    if (!currentDate) return
    const delta = dir === 'prev' ? -1 : 1
    switch (viewType) {
      case 'month':
        setCurrentDate(addMonths(currentDate, delta))
        break
      case 'week':
      case 'agenda':
        setCurrentDate(addWeeks(currentDate, delta))
        break
      case 'day':
        setCurrentDate(addDays(currentDate, delta))
        break
    }
  }

  const headerTitle = useMemo(() => {
    if (!currentDate) return ''
    switch (viewType) {
      case 'month':
        return format(currentDate, 'MMMM yyyy')
      case 'week': {
        // 7-day rolling window starting at currentDate.
        const wkStart = startOfDay(currentDate)
        const wkEnd = addDays(wkStart, 6)
        if (isSameMonth(wkStart, wkEnd)) {
          return `${format(wkStart, 'MMM d')} – ${format(wkEnd, 'd, yyyy')}`
        }
        return `${format(wkStart, 'MMM d')} – ${format(wkEnd, 'MMM d, yyyy')}`
      }
      case 'day':
        return format(currentDate, 'EEEE, MMMM d, yyyy')
      case 'agenda':
        return `Week of ${format(startOfDay(currentDate), 'MMM d, yyyy')}`
    }
  }, [currentDate, viewType])

  // ─── dnd ───
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
  )

  const handleDragStart = (e: DragStartEvent) => {
    const data = e.active.data.current as
      | TaskDragData
      | ExistingEventDragData
      | LocationPillDragData
      | undefined
    if (data?.kind === TASK_DRAG_TYPE) {
      setActiveDragTask(data.task)
    } else if (data?.kind === EVENT_DRAG_TYPE) {
      setActiveDragEvent(data.event)
    } else if (data?.kind === LOCATION_PILL_DRAG_TYPE) {
      // Treat a location-pill drag as if it were a task drag for the
      // overlay/drop-zone activation paths. The pill-stays-in-lane rule
      // is handled in onDragEnd (we never remove it from `tasks`).
      setActiveDragTask(data.task)
    }
  }

  const handleDragEnd = async (e: DragEndEvent) => {
    const activeData = e.active.data.current as
      | TaskDragData
      | ExistingEventDragData
      | LocationPillDragData
      | undefined
    const overData = e.over?.data.current as
      | DayDropData
      | TimeSlotDropData
      | BufferDropData
      | undefined
    // Always clear drag-overlay state before we touch the network; any UI
    // follow-up (loading spinner) belongs on the target, not the ghost.
    const wasTask =
      activeData?.kind === TASK_DRAG_TYPE
        ? activeData.task
        : activeData?.kind === LOCATION_PILL_DRAG_TYPE
          ? activeData.task
          : null
    const wasLocationPill = activeData?.kind === LOCATION_PILL_DRAG_TYPE
    const wasEvent = activeData?.kind === EVENT_DRAG_TYPE ? activeData.event : null
    setActiveDragTask(null)
    setActiveDragEvent(null)
    setHoveredDayIso(null)
    clearDwell()

    if (!activeData || !overData) return

    // Buffer drops short-circuit: zoom-out is a pure view swap (also fired
    // on dwell, this catches the explicit "drop here" case); unschedule
    // deletes the underlying schedule event.
    if (overData.kind === BUFFER_DROP_TYPE) {
      if (overData.action === 'zoom_out') {
        if (viewType !== 'month') zoomOutOneLevel()
        return
      }
      if (overData.action === 'unschedule' && wasEvent) {
        await handleUnschedule(wasEvent.itemId, wasEvent.title)
      }
      return
    }

    // Location pill: if the task already has a schedule event, just flag
    // that and bail. "Drop it to create another copy" would be confusing;
    // honour the existing commitment instead.
    if (wasLocationPill && wasTask) {
      const alreadyScheduled = events.some(
        (ev) => ev.parent_task_id === wasTask.id,
      )
      if (alreadyScheduled) {
        toast.info(
          'Already scheduled',
          'This task has an existing schedule entry. Move it from the grid to reschedule.',
        )
        return
      }
    }

    // Resolve (dateIso, start_time). For day drops during a time-slot-capable
    // view we still accept a day-level fallback → 09:00 for new tasks, or
    // "preserve original minute-of-day" for reschedules.
    let dateIso: string
    let startTime: string | null
    if (overData.kind === DAY_DROP_TYPE) {
      dateIso = overData.dateIso
      startTime = null // signal: decide per drag-source below
    } else if (overData.kind === TIME_SLOT_DROP_TYPE) {
      dateIso = overData.dateIso
      startTime = minutesToTimeString(overData.startMinute)
    } else {
      return
    }

    if (wasTask) {
      const task = wasTask
      const [yy, mm0, dd] = dateIso.split('-').map(Number)
      const targetDay = new Date(yy, mm0 - 1, dd)
      const effectiveStart = startTime ?? defaultStartForDay(targetDay, now)
      const duration = task.estimated_duration ?? 30

      // Optimistic insert: render a provisional event with a tempId so
      // the user sees the drop land immediately *before* the confirmation
      // modal appears. Commit/cancel decide the event's fate via
      // handleConfirmDrop / handleCancelDrop.
      const [hh, mm] = effectiveStart.split(':').map(Number)
      const optimisticStart = setMinutes(
        setHours(new Date(yy, mm0 - 1, dd), hh || 9),
        mm || 0,
      )
      const optimisticEnd = new Date(optimisticStart.getTime() + duration * 60_000)
      const tempId = `optimistic-${Date.now()}-${task.id}`
      const rollback = mutate((prev) => [
        ...prev,
        {
          id: tempId,
          title: task.title,
          description: task.description ?? undefined,
          start: optimisticStart,
          end: optimisticEnd,
          allDay: false,
          color: EVENT_COLORS.task,
          type: 'schedule',
          source: 'horton',
          parent_task_id: task.id,
        },
      ])

      // Stage the drop; the confirmation dialog drives commit/cancel.
      const drop: PendingDrop = {
        tempId,
        rollback,
        task,
        dateIso,
        startTime: effectiveStart,
        duration,
        wasTimeSlot: overData.kind === TIME_SLOT_DROP_TYPE,
        targetDate: optimisticStart,
      }
      pendingDropRef.current = drop
      setPendingDrop(drop)
      return
    }

    if (wasEvent) {
      const ev = wasEvent
      // Reschedule: preserve duration; if the drop target is a day-cell
      // (month view), keep the same start minute-of-day.
      const originalStartMin = ev.start.getHours() * 60 + ev.start.getMinutes()
      const effectiveStart =
        startTime ?? minutesToTimeString(originalStartMin - (originalStartMin % 30))
      const [hh, mm] = effectiveStart.split(':').map(Number)

      // Optimistic reschedule: immediately move the event in local state
      // so the drop "sticks" visually. The save is no longer silent —
      // we open the edit modal with the new date/time pre-filled, the
      // diff indicators pointing at the pre-drag values, and the
      // rollback armed so Cancel snaps the pill back.
      const [y, m, d] = dateIso.split('-').map(Number)
      const newStart = setMinutes(setHours(new Date(y, m - 1, d), hh), mm)
      const newEnd = new Date(newStart.getTime() + ev.durationMin * 60_000)
      const rollback = mutate((prev) =>
        prev.map((p) =>
          p.id === ev.id ? { ...p, start: newStart, end: newEnd } : p,
        ),
      )

      const source = events.find((e) => e.id === ev.id)
      if (!source || !isEventEditable(source)) {
        // Can't edit this kind of event (e.g. Google Calendar sync) —
        // roll the optimistic move back and toast. The pill was never
        // draggable in the first place in this branch, but we keep the
        // guard so we don't silently drop edits.
        rollback()
        toast.error("Can't move this event", 'Not editable from this view')
        return
      }
      beginEditing(
        source,
        buildInitial(source, {
          date: dateIso,
          startTime: effectiveStart,
          durationMin: ev.durationMin,
        }),
        rollback,
      )
      return
    }
  }

  const handleOpenTaskFromPill = useCallback(
    (taskId: number) => {
      navigate(`/tasks/${taskId}`)
    },
    [navigate],
  )

  // ─── Event edit modal ────────────────────────────────────────────────
  // `editingEvent` is the ScheduleEvent the user is currently editing
  // (via click OR drag-drop). `editingInitial` is what the modal
  // compares against for the "was X" diff hints. When a drag-drop
  // triggers the modal we also stash a rollback so Cancel can undo
  // the optimistic move.
  const [editingEvent, setEditingEvent] = useState<ScheduleEvent | null>(null)
  const [editingInitial, setEditingInitial] = useState<EventEditInitial | null>(null)
  const [editingRollback, setEditingRollback] = useState<(() => void) | null>(null)
  const [savingEdit, setSavingEdit] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)
  // Commitment of the block being edited — what decides whether the SC-7
  // "keep it flexible" opt-out is on offer. Starts at whatever the list
  // payload knew (usually nothing) and is replaced by the single-block
  // read model below.
  const [editingCommitment, setEditingCommitment] = useState<CommitmentState | null>(null)
  // Guards against a slow lookup landing on a block the user already
  // closed — a stale answer would offer "keep flexible" for the wrong row.
  const commitmentLookupRef = useRef(0)

  // Helper: is this event something we can edit-save? Google events and
  // items without any identifying id (neither item_id nor master_id) get
  // read-only pills. Recurring instances ARE editable as long as we
  // know their master id.
  const isEventEditable = useCallback((e: ScheduleEvent): boolean => {
    if (e.source === 'google_calendar') return false
    if (typeof e.item_id === 'number') return true
    if (typeof e.master_schedule_id === 'number') return true
    return false
  }, [])

  // Turn a ScheduleEvent into the shape the modal needs. Optionally
  // overlays a `proposed` patch — used by drag-drop to pre-fill the new
  // date/time while keeping the "was X" diffs pointed at the original.
  const buildInitial = useCallback(
    (
      ev: ScheduleEvent,
      proposed?: Partial<EventEditValues>,
    ): EventEditInitial => {
      const y = ev.start.getFullYear()
      const m = String(ev.start.getMonth() + 1).padStart(2, '0')
      const d = String(ev.start.getDate()).padStart(2, '0')
      const hh = String(ev.start.getHours()).padStart(2, '0')
      const mm = String(ev.start.getMinutes()).padStart(2, '0')
      const endMs = (ev.end ?? ev.start).getTime()
      const durationMin = Math.max(
        5,
        Math.round((endMs - ev.start.getTime()) / 60_000),
      )
      const isRecurring =
        !!ev.is_recurring_master ||
        !!ev.is_recurring_instance ||
        ev.task_kind === 'habit_occurrence' ||
        !!ev.recurring_source_id
      let recurrenceLabel: string | undefined
      if (ev.task_kind === 'habit_occurrence') recurrenceLabel = 'Habit occurrence'
      else if (ev.is_recurring_master) recurrenceLabel = 'Recurring master'
      else if (ev.is_recurring_instance) recurrenceLabel = 'Recurring occurrence'
      return {
        title: ev.title,
        description: ev.description,
        date: `${y}-${m}-${d}`,
        startTime: `${hh}:${mm}`,
        durationMin,
        isRecurring,
        recurrenceLabel,
        proposed,
      }
    },
    [],
  )

  // Opens the modal on one block and asks the single-block read model what
  // that block's commitment actually is.
  //
  // The list payload carries the pair, so the pill usually already knows.
  // This asks anyway, for the one block the user just opened: `why` is the
  // authoritative single-block read model, the modal is where the SC-7
  // opt-out is decided, and a stale list row is the worst place to decide
  // it from. Cheap and bounded — one request for one block the user is
  // looking at, never a per-row fan-out in a list view. The answer is
  // folded back into the events list so the pill behind the modal agrees
  // with the modal. An unknown answer stays unknown: no chip, no opt-out.
  const beginEditing = useCallback(
    (
      source: ScheduleEvent,
      initial: EventEditInitial,
      rollback: (() => void) | null,
    ) => {
      setEditingEvent(source)
      setEditingInitial(initial)
      setEditingRollback(rollback ? () => rollback : null)
      setEditError(null)
      setEditingCommitment(source.commitment ?? null)

      const token = commitmentLookupRef.current + 1
      commitmentLookupRef.current = token
      // Virtual occurrences have no row of their own; their commitment is
      // the master's (the exception they become inherits it).
      const lookupId =
        typeof source.item_id === 'number'
          ? source.item_id
          : typeof source.master_schedule_id === 'number'
          ? source.master_schedule_id
          : null
      if (lookupId == null) return
      void fetchBlockCommitment(lookupId).then(({ state }) => {
        if (commitmentLookupRef.current !== token) return
        setEditingCommitment(state)
        if (state) {
          mutate((prev) =>
            prev.map((p) => (p.id === source.id ? { ...p, commitment: state } : p)),
          )
        }
      })
    },
    [mutate],
  )

  // Opens the edit modal for a given React event id. The React id is
  // always defined (virtual recurring instances also carry one), unlike
  // `item_id` which is undefined for virtuals — that's why we match on
  // `id` here. Silently no-ops if the event is no longer on screen
  // (rare — navigation races).
  const openEventEditor = useCallback(
    (eventId: string) => {
      const target = events.find((e) => e.id === eventId)
      if (!target) return
      if (!isEventEditable(target)) return
      beginEditing(target, buildInitial(target), null)
    },
    [events, isEventEditable, buildInitial, beginEditing],
  )

  // Save-dispatch: routes the user's edits + chosen scope to the right
  // API. We do the simplest correct thing per case and let the caller
  // refetch on success to reconcile UI state.
  //
  //   - single (non-recurring real event) -> PUT /api/schedule/events/{id}
  //   - this_occurrence (recurring master/instance) -> POST the master's
  //     /exception endpoint with original_date + new fields
  //   - all_occurrences + habit -> PUT /api/hierarchy/habits/{habit_id}
  //   - all_occurrences + recurring master/instance -> PUT the master's
  //     schedule entry with the new fields
  //
  // SC-7 rides along on the two placement paths: `keepFlexible` becomes
  // `keep_flexible: true`, the server's explicit opt-out from
  // confirm-on-placement-change. Absent (the default) the block confirms,
  // which is the ratified semantic. The habit path has no placement
  // commitment to keep — habit occurrences are confirmed-via-rule.
  const saveEventEdit = useCallback(
    async (scope: SaveScope, edits: EventEditValues) => {
      const ev = editingEvent
      if (!ev) return
      setSavingEdit(true)
      setEditError(null)
      try {
        const [hh, mm] = edits.startTime.split(':').map(Number)
        const endMinAbs = (hh || 0) * 60 + (mm || 0) + edits.durationMin
        const endHH = String(Math.floor(endMinAbs / 60) % 24).padStart(2, '0')
        const endMM = String(endMinAbs % 60).padStart(2, '0')
        const timeSlot = `${edits.startTime}-${endHH}:${endMM}`
        let outcome: MutationOutcome

        if (scope === 'all_occurrences' && ev.task_kind === 'habit_occurrence' && ev.recurring_source_id) {
          // Habits live in Postgres; their scheduled start/duration/title
          // are authoritative there. Push the matching fields back to
          // the habit definition.
          outcome = await updateHabitDefinition(ev.recurring_source_id, {
            title: edits.title,
            description: edits.description,
            startTime: edits.startTime,
            durationMin: edits.durationMin,
          })
        } else if (scope === 'this_occurrence') {
          // Recurring master/instance -> create an exception on the
          // master id. If we were clicked on an instance, its
          // original_date is either the instance's start date or the
          // server-provided original_date.
          const masterId = ev.master_schedule_id ?? ev.item_id
          if (typeof masterId !== 'number') {
            throw new Error("Can't find master for this occurrence")
          }
          const originalDate =
            ev.original_date ??
            (() => {
              const y = ev.start.getFullYear()
              const m = String(ev.start.getMonth() + 1).padStart(2, '0')
              const d = String(ev.start.getDate()).padStart(2, '0')
              return `${y}-${m}-${d}`
            })()
          outcome = await createOccurrenceException(masterId, originalDate, {
            title: edits.title,
            description: edits.description,
            date: edits.date,
            timeSlot,
            durationMin: edits.durationMin,
            keepFlexible: edits.keepFlexible,
          })
        } else {
          // single OR all_occurrences on a non-habit recurring master.
          // Both hit the same PUT endpoint — "all occurrences" on the
          // master implicitly updates every future expansion.
          const targetId =
            scope === 'all_occurrences'
              ? ev.master_schedule_id ?? ev.item_id
              : ev.item_id
          if (typeof targetId !== 'number') {
            throw new Error('Missing event id')
          }
          outcome = await updateScheduleEvent(targetId, {
            title: edits.title,
            description: edits.description,
            date: edits.date,
            timeSlot,
            durationMin: edits.durationMin,
            keepFlexible: edits.keepFlexible,
          })
        }

        const scopeDetail =
          scope === 'all_occurrences'
            ? 'All occurrences updated'
            : scope === 'this_occurrence'
            ? 'This occurrence updated'
            : 'Event updated'
        if (outcome.pending) {
          // 202 applied:false — accepted, not applied. Don't claim it moved.
          toast.info('Change accepted', PENDING_CHANGE_COPY)
        } else if (edits.keepFlexible) {
          toast.success(
            'Moved',
            `Still flexible — ${PRODUCT_NAME} can keep adjusting it.`,
          )
        } else {
          toast.success('Saved', scopeDetail)
        }
        setEditingEvent(null)
        setEditingInitial(null)
        setEditingRollback(null)
        setEditingCommitment(null)
        void refetch()
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'unknown error'
        setEditError(msg)
        // Don't auto-rollback here — the user sees the error in-modal
        // and can retry. Cancel still works, and rolls back the drag
        // if one was staged.
      } finally {
        setSavingEdit(false)
      }
    },
    [editingEvent, refetch, toast],
  )

  // Cancel path for the edit modal. When the modal was opened by a
  // drag-drop, we also undo the optimistic insert so the pill snaps
  // back to where it was.
  const cancelEventEdit = useCallback(() => {
    if (editingRollback) {
      try {
        editingRollback()
      } catch (err) {
        console.error('[schedulev2] rollback failed', err)
      }
    }
    setEditingEvent(null)
    setEditingInitial(null)
    setEditingRollback(null)
    setSavingEdit(false)
    setEditError(null)
    setEditingCommitment(null)
  }, [editingRollback])

  // Commits a staged drop: POSTs to the schedule API, then swaps the
  // optimistic pill's id/item_id with the server's real id IN PLACE.
  // We deliberately do NOT call refetch() here — the POST response has
  // enough data to finalize the pill, and a full refetch was the source
  // of the "pill disappears after drop" bug (the virtual expansion read
  // occasionally returned stale data and clobbered the optimistic entry).
  const handleConfirmDrop = useCallback(async () => {
    const drop = pendingDropRef.current
    if (!drop) return
    // Claim it synchronously so ConfirmationModal's follow-up onClose
    // (which will fire in the same click) finds the ref null and no-ops.
    pendingDropRef.current = null
    setPendingDrop(null)
    setPendingMutations((n) => n + 1)
    try {
      const res = await fetch('/api/schedule/events', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: drop.task.title,
          date: drop.dateIso,
          start_time: drop.startTime,
          duration: drop.duration,
          parent_task_id: drop.task.id,
          category: 'task',
          description: drop.task.description ?? '',
        }),
      })
      const body = await res.json().catch(() => null)
      if (!res.ok || !body?.success) {
        throw new Error(body?.error || `HTTP ${res.status}`)
      }

      const serverItemId =
        (body?.event && (body.event.item_id as number | undefined)) ??
        (body?.event && typeof body.event.id === 'number' ? (body.event.id as number) : undefined)

      if (typeof serverItemId === 'number') {
        // Attach the real item_id to the optimistic pill so unschedule/
        // reschedule PUT/DELETE calls reach the right row. We keep the
        // React `id` (= tempId) stable so the pill's DraggableEventBlock
        // doesn't remount and flicker; a later refetch will harmonise
        // the id to `schedule-{serverItemId}` without user-visible cost.
        // The create route stamps the user-placement pair unconditionally
        // (CONFIRMED / USER_DIRECT — the user chose these coordinates), so
        // the pill can say so without waiting for a read.
        mutate((prev) =>
          prev.map((p) =>
            p.id === drop.tempId
              ? { ...p, item_id: serverItemId, commitment: 'confirmed' as const }
              : p,
          ),
        )
        setRecentlyCreated((m) => ({ ...m, [serverItemId]: Date.now() }))
      } else {
        // Server didn't hand back a usable id — fall back to refetch so
        // we at least converge. (Unexpected path for this endpoint.)
        void refetch()
      }

      const when = drop.wasTimeSlot
        ? `${format(parseISO(drop.dateIso), 'MMM d')} at ${drop.startTime}`
        : format(parseISO(drop.dateIso), 'MMM d')
      toast.success('Scheduled', `"${drop.task.title}" on ${when}`)
    } catch (err) {
      console.error('[schedulev2] schedule create failed', err)
      drop.rollback()
      toast.error(
        "Couldn't schedule",
        err instanceof Error ? err.message : 'unknown error',
      )
    } finally {
      setPendingMutations((n) => Math.max(0, n - 1))
    }
  }, [mutate, refetch, toast])

  // Cancels a staged drop: rolls back the optimistic pill and returns
  // the task to the unscheduled pool (deriveUnscheduled picks it up
  // automatically once no event references its id).
  const handleCancelDrop = useCallback(() => {
    const drop = pendingDropRef.current
    if (!drop) return
    pendingDropRef.current = null
    drop.rollback()
    setPendingDrop(null)
    toast.info('Kept in Unscheduled', `"${drop.task.title}" is still in your sidebar.`)
  }, [toast])

  // Remove a block from the calendar — the X on a pill and the drag-to-
  // buffer gesture both land here.
  //
  // The verb is not the same verb for both commitments, and it stopped
  // being safe to pretend otherwise once Flexible holds started rendering
  // on this page (see the hold read above).
  //
  //   CONFIRMED (or unknown) → DELETE. The user placed it, the user takes
  //   it back, and the row is theirs to remove.
  //
  //   FLEXIBLE → "not today" (`skip-today`). Deleting a hold hard-deletes
  //   a row yanamas wrote, which SC-12 forbids — holds are expired, never
  //   deleted, so the history survives — and it throws away the SC-16
  //   outcome that says why it left the plan. `skip-today` records
  //   `skipped_by_user`, takes the block off this day, and leaves every
  //   other day of a recurring hold alone. It is also the truer reading of
  //   the gesture: a hold is yanamas' proposal for this slot, and dropping
  //   it means "not this", not "never".
  const handleUnschedule = useCallback(
    async (itemId: number, titleHint?: string) => {
      const prev = events.find((e) => e.item_id === itemId)
      const title = titleHint ?? prev?.title ?? 'Event'
      const flexible = prev?.commitment === 'flexible'
      const rollback = mutate((list) => list.filter((e) => e.item_id !== itemId))
      setPendingMutations((n) => n + 1)
      try {
        if (flexible && prev) {
          const y = prev.start.getFullYear()
          const m = String(prev.start.getMonth() + 1).padStart(2, '0')
          const d = String(prev.start.getDate()).padStart(2, '0')
          const result = await removeBlockFromDay(itemId, `${y}-${m}-${d}`)
          if (!result.ok) {
            throw new Error(result.error || 'Could not reach the scheduler.')
          }
          if (result.applied) {
            // Deliberately not "back in your unscheduled list" — the item
            // is not unscheduled, it is off THIS day. Saying otherwise
            // would report a change that did not happen.
            toast.success('Not today', `"${title}" is off today's plan.`)
          } else {
            toast.info('Change accepted', result.message ?? PENDING_CHANGE_COPY)
          }
          void refetch()
          return
        }

        const res = await fetch(`/api/schedule/events/${itemId}`, {
          method: 'DELETE',
          credentials: 'include',
        })
        if (!res.ok) {
          const body = await res.json().catch(() => null)
          throw new Error(body?.error || `HTTP ${res.status}`)
        }
        toast.success('Unscheduled', `"${title}" is back in your unscheduled list.`)
        // Drop the grace-window marker; the pill is gone anyway.
        setRecentlyCreated((m) => {
          if (!(itemId in m)) return m
          const next = { ...m }
          delete next[itemId]
          return next
        })
        void refetch()
      } catch (err) {
        console.error('[schedulev2] unschedule failed', err)
        rollback()
        toast.error(
          flexible ? "Couldn't take it off today" : "Couldn't unschedule",
          err instanceof Error ? err.message : 'unknown error',
        )
      } finally {
        setPendingMutations((n) => Math.max(0, n - 1))
      }
    },
    [events, mutate, refetch, toast],
  )

  const clearDwell = useCallback(() => {
    if (dwellTimerRef.current != null) {
      window.clearTimeout(dwellTimerRef.current)
      dwellTimerRef.current = null
    }
    dwellKeyRef.current = null
  }, [])

  // Walk the view stack one level outward.
  const zoomOutOneLevel = useCallback(() => {
    setViewType((v) => (v === 'day' ? 'week' : v === 'week' ? 'month' : v))
  }, [])

  const handleDragMove = (e: DragMoveEvent) => {
    const overData = e.over?.data.current as
      | DayDropData
      | TimeSlotDropData
      | BufferDropData
      | undefined
    if (overData?.kind === DAY_DROP_TYPE || overData?.kind === TIME_SLOT_DROP_TYPE) {
      setHoveredDayIso(overData.dateIso)
    } else {
      setHoveredDayIso(null)
    }

    // Dwell-expand: hovering a month-view day cell with a task drag for
    // ~600 ms swaps to that day's view so the user can pick a time slot.
    if (
      viewType === 'month' &&
      activeDragTask &&
      overData?.kind === DAY_DROP_TYPE
    ) {
      const key = `month:${overData.dateIso}`
      if (dwellKeyRef.current !== key) {
        clearDwell()
        const iso = overData.dateIso
        dwellKeyRef.current = key
        dwellTimerRef.current = window.setTimeout(() => {
          const [y, m, d] = iso.split('-').map(Number)
          setCurrentDate(new Date(y, m - 1, d))
          setViewType('day')
          dwellTimerRef.current = null
          dwellKeyRef.current = null
        }, 600)
      }
      return
    }

    // Buffer zoom-out region: dwell ~500 ms while dragging an existing
    // event triggers the next zoom-out level.
    if (
      activeDragEvent &&
      overData?.kind === BUFFER_DROP_TYPE &&
      overData.action === 'zoom_out' &&
      viewType !== 'month'
    ) {
      const key = `buf:zoom_out:${viewType}`
      if (dwellKeyRef.current !== key) {
        clearDwell()
        dwellKeyRef.current = key
        dwellTimerRef.current = window.setTimeout(() => {
          zoomOutOneLevel()
          dwellTimerRef.current = null
          dwellKeyRef.current = null
        }, 500)
      }
      return
    }

    // Pointer left any dwell-eligible target → cancel.
    if (dwellKeyRef.current != null) clearDwell()
  }

  // ─── derived day ranges ───
  const calendarDays = useMemo(() => {
    if (!currentDate) return [] as Date[]
    const start = startOfWeek(startOfMonth(currentDate))
    const end = endOfWeek(endOfMonth(currentDate))
    const days: Date[] = []
    let d = start
    while (d <= end) {
      days.push(d)
      d = addDays(d, 1)
    }
    return days
  }, [currentDate])

  // Week view shows a 7-day rolling strip starting at `currentDate`. The
  // user's expectation is that "today" (or whatever day they navigated
  // to) is always the leftmost column, not the Sunday/Monday of a
  // calendar week. Prev/Next step the window by 7 days so the alignment
  // persists on navigation.
  const weekDays = useMemo(() => {
    if (!currentDate) return [] as Date[]
    const start = startOfDay(currentDate)
    return Array.from({ length: 7 }, (_, i) => addDays(start, i))
  }, [currentDate])

  const getEventsForDay = useCallback(
    (day: Date) => events.filter((e) => isSameDay(e.start, day)),
    [events],
  )

  if (serverTimeLoading || !currentDate) {
    return (
      <div className="h-full flex items-center justify-center bg-default">
        <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
      </div>
    )
  }

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragMove={handleDragMove}
      accessibility={{
        announcements: {
          onDragStart: ({ active }) => {
            const data = active.data.current as
              | TaskDragData
              | ExistingEventDragData
              | LocationPillDragData
              | undefined
            if (data?.kind === TASK_DRAG_TYPE || data?.kind === LOCATION_PILL_DRAG_TYPE) {
              return `Picked up task ${data.task.title}. Drop on a day or time slot to schedule.`
            }
            if (data?.kind === EVENT_DRAG_TYPE) {
              return `Picked up event ${data.event.title}. Drop on a new time slot to reschedule.`
            }
            return 'Picked up item.'
          },
          onDragOver: ({ over }) => {
            const d = over?.data.current as
              | DayDropData
              | TimeSlotDropData
              | BufferDropData
              | undefined
            if (d?.kind === DAY_DROP_TYPE) return `Over ${d.dateIso}`
            if (d?.kind === TIME_SLOT_DROP_TYPE) {
              return `Over ${d.dateIso} at ${minutesToTimeString(d.startMinute)}`
            }
            if (d?.kind === BUFFER_DROP_TYPE) {
              return d.action === 'zoom_out'
                ? 'Hold to zoom out to a wider view.'
                : 'Release to unschedule.'
            }
            return 'No drop target.'
          },
          onDragEnd: ({ over }) => {
            const d = over?.data.current as { kind?: string; action?: string } | undefined
            if (d?.kind === BUFFER_DROP_TYPE) {
              return d.action === 'unschedule' ? 'Unscheduled.' : 'Zoomed out.'
            }
            return over ? 'Dropped.' : 'Cancelled — no target.'
          },
          onDragCancel: () => 'Drag cancelled.',
        },
      }}
    >
      <div className="h-full min-h-0 flex flex-col bg-default">
        {/* The preview banner is retired: this IS the schedule (owner ask
            2026-08-25 — schedule-v2 became canonical in E12). */}

        {/* Header */}
        <header className="bg-surface border-b border-soft px-2 sm:px-4 py-2 sm:py-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 sm:gap-4 min-w-0 flex-1">
              <div className="hidden lg:flex items-center gap-2">
                <div className="p-2 bg-brand-primary rounded-lg">
                  <Calendar className="w-6 h-6 text-on-brand" />
                </div>
                <span className="text-xl font-semibold text-primary">Schedule</span>
              </div>

              {/* Mobile-only Tasks drawer toggle. Hidden on lg+ where the
                  sidebar sits inline. */}
              <button
                type="button"
                onClick={() => setSidebarCollapsed((c) => !c)}
                className="lg:hidden inline-flex items-center gap-1 px-2 py-1 rounded-md border border-soft bg-surface-alt text-xs font-medium text-primary hover:bg-surface"
                aria-label={sidebarCollapsed ? 'Show unscheduled tasks' : 'Hide unscheduled tasks'}
                aria-expanded={!sidebarCollapsed}
              >
                <Inbox className="w-4 h-4" />
                <span>Tasks</span>
                {unscheduledTasks.length > 0 && (
                  <span className="ml-0.5 text-app-nano font-normal text-muted">
                    ({unscheduledTasks.length})
                  </span>
                )}
              </button>

              <Button onClick={goToToday} variant="outline" size="sm">
                Today
              </Button>

              <div className="flex items-center">
                <button
                  onClick={() => nav('prev')}
                  className="p-1.5 sm:p-2 hover:bg-surface-alt rounded-full"
                  aria-label="Previous"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => nav('next')}
                  className="p-1.5 sm:p-2 hover:bg-surface-alt rounded-full"
                  aria-label="Next"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              <h1 className="text-sm sm:text-xl font-semibold text-primary truncate min-w-0">
                {headerTitle}
              </h1>

              {pendingMutations > 0 && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-subtle text-brand-primary text-xs font-medium"
                  role="status"
                  aria-live="polite"
                >
                  <Loader2 className="h-3 w-3 animate-spin" />
                  <span className="hidden sm:inline">Saving…</span>
                </span>
              )}
            </div>

            <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
              <button
                onClick={() => navigate('/review-schedule')}
                className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 text-sm font-medium rounded-lg bg-brand text-on-brand hover:shadow-md transition-all"
                title="Plan tasks with AI"
              >
                <Sparkles className="w-4 h-4" />
                <span className="hidden sm:inline">Plan</span>
              </button>

              <div className="flex items-center bg-surface-alt rounded-lg p-0.5 sm:p-1">
                {(['month', 'week', 'day', 'agenda'] as ViewType[]).map((v) => (
                  <button
                    key={v}
                    onClick={() => setViewType(v)}
                    className={`px-1.5 sm:px-3 py-0.5 sm:py-1 text-xs sm:text-sm font-medium rounded capitalize transition-colors ${
                      viewType === v
                        ? 'bg-surface text-primary shadow-sm'
                        : 'text-muted hover:text-primary'
                    }`}
                  >
                    {/* One-letter labels on narrow screens to keep the
                        switcher compact without hiding any view. */}
                    <span className="sm:hidden">{v[0].toUpperCase()}</span>
                    <span className="hidden sm:inline">{v}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </header>

        {/* Body: sidebar + main. Positioned relative so the mobile drawer
            overlays only the calendar area (not the top bar / bottom nav
            from MobileLayout). */}
        <div className="relative flex flex-1 min-h-0 overflow-hidden">
          {/* Mobile drawer backdrop. Tapping it closes the drawer. Hidden
              on lg+ where the sidebar is inline. */}
          {!sidebarCollapsed && (
            <button
              type="button"
              aria-label="Close unscheduled tasks"
              onClick={() => setSidebarCollapsed(true)}
              className="lg:hidden absolute inset-0 z-30 bg-black/40"
            />
          )}

          {/* Sidebar wrapper. Desktop: inline column. Mobile: overlay
              drawer absolutely positioned within the body. */}
          <div
            className={[
              sidebarCollapsed ? 'hidden lg:block' : 'block',
              'lg:static lg:shadow-none',
              'max-lg:absolute max-lg:inset-y-0 max-lg:left-0 max-lg:z-40',
              'max-lg:shadow-xl max-lg:max-w-[85vw]',
            ].join(' ')}
          >
            <UnscheduledTasksSidebar
              tasks={unscheduledTasks}
              loading={tasksLoading}
              draggingTaskId={activeDragTask?.id ?? null}
              highlightTaskId={focusTaskId}
              collapsed={sidebarCollapsed}
              onToggleCollapse={() => setSidebarCollapsed((c) => !c)}
            />
          </div>

          <main className="flex-1 min-w-0 overflow-auto">
            {/* Unschedule / zoom-out buffer — only while dragging an
                existing schedule event. Hidden otherwise so it never
                steals clicks. */}
            {activeDragEvent && (
              <UnscheduleBuffer canZoomOut={viewType !== 'month'} />
            )}
            {eventsLoading && events.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
              </div>
            ) : (
              <>
                {viewType === 'month' && (
                  <MonthView
                    calendarDays={calendarDays}
                    currentDate={currentDate}
                    serverDate={serverDate || currentDate}
                    now={now}
                    getEventsForDay={getEventsForDay}
                    hoveredDayIso={hoveredDayIso}
                    isDragging={isAnyDragging}
                    locationCountForDay={locationCountForDay}
                    onDayClick={(d) => {
                      setCurrentDate(d)
                      setViewType('day')
                    }}
                    recentlyCreated={recentlyCreated}
                    onUnschedule={handleUnschedule}
                  />
                )}
                {viewType === 'week' && (
                  <WeekView
                    weekDays={weekDays}
                    serverDate={serverDate || currentDate}
                    now={now}
                    getEventsForDay={getEventsForDay}
                    isDragging={isAnyDragging}
                    isEventDraggable={isEventDraggable}
                    isEventEditable={isEventEditable}
                    locationTasks={locationTasks}
                    onOpenTaskFromPill={handleOpenTaskFromPill}
                    onOpenEventEditor={openEventEditor}
                    recentlyCreated={recentlyCreated}
                    onUnschedule={handleUnschedule}
                  />
                )}
                {viewType === 'day' && (
                  <DayView
                    currentDate={currentDate}
                    serverDate={serverDate || currentDate}
                    now={now}
                    getEventsForDay={getEventsForDay}
                    isDragging={isAnyDragging}
                    isEventDraggable={isEventDraggable}
                    isEventEditable={isEventEditable}
                    locationTasks={locationTasks}
                    onOpenTaskFromPill={handleOpenTaskFromPill}
                    onOpenEventEditor={openEventEditor}
                    recentlyCreated={recentlyCreated}
                    onUnschedule={handleUnschedule}
                  />
                )}
                {viewType === 'agenda' && (
                  <AgendaView
                    weekDays={weekDays}
                    serverDate={serverDate || currentDate}
                    now={now}
                    getEventsForDay={getEventsForDay}
                  />
                )}
              </>
            )}
          </main>
        </div>
      </div>

      <ConfirmationModal
        isOpen={pendingDrop != null}
        onClose={handleCancelDrop}
        onConfirm={handleConfirmDrop}
        title="Schedule this task?"
        variant="confirm"
        confirmText="Schedule"
        cancelText="Cancel"
        message={
          pendingDrop ? (
            <div className="space-y-2">
              <p>
                <span className="font-medium text-primary">
                  &ldquo;{pendingDrop.task.title}&rdquo;
                </span>
              </p>
              <p className="text-secondary">
                {pendingDrop.wasTimeSlot
                  ? `${format(pendingDrop.targetDate, 'EEE, MMM d')} at ${pendingDrop.startTime}`
                  : `${format(pendingDrop.targetDate, 'EEE, MMM d')} · around ${pendingDrop.startTime}`}
                <span className="text-muted"> · {pendingDrop.duration} min</span>
              </p>
              <p className="text-xs text-muted">
                Cancel to send this task back to the Unscheduled sidebar.
              </p>
            </div>
          ) : null
        }
      />

      <EventEditModal
        open={editingEvent != null}
        initial={editingInitial}
        saving={savingEdit}
        error={editError}
        commitment={editingCommitment}
        onSave={saveEventEdit}
        onCancel={cancelEventEdit}
      />

      <DragOverlay dropAnimation={null}>
        {activeDragTask ? (
          <div className="pointer-events-none opacity-90 w-64">
            <DraggableTaskCard task={activeDragTask} />
          </div>
        ) : activeDragEvent ? (
          <div
            className="pointer-events-none opacity-90 rounded px-2 py-1 text-xs shadow-md"
            style={{
              width: '180px',
              backgroundColor:
                activeDragEvent.color + commitmentFillAlpha(activeDragEvent.commitment),
              color: activeDragEvent.color,
              borderLeft: `3px ${commitmentBorder(activeDragEvent.commitment)} ${activeDragEvent.color}`,
            }}
          >
            <div className="flex items-center gap-1">
              <CommitmentGlyph state={activeDragEvent.commitment} className="w-2.5 h-2.5" />
              <span className="font-medium truncate">{activeDragEvent.title}</span>
            </div>
            <div className="text-app-micro opacity-80">
              {format(activeDragEvent.start, 'h:mm a')} · {activeDragEvent.durationMin}m
            </div>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}

// ───────── sub views ─────────

interface MonthViewProps {
  calendarDays: Date[]
  currentDate: Date
  serverDate: Date
  now: Date
  getEventsForDay: (d: Date) => ScheduleEvent[]
  hoveredDayIso: string | null
  isDragging: boolean
  locationCountForDay: (d: Date) => number
  onDayClick: (d: Date) => void
  recentlyCreated: Record<number, number>
  onUnschedule: (itemId: number, title?: string) => void
}

const MonthView: React.FC<MonthViewProps> = ({
  calendarDays,
  currentDate,
  serverDate,
  now,
  getEventsForDay,
  hoveredDayIso,
  isDragging,
  locationCountForDay,
  onDayClick,
  recentlyCreated,
  onUnschedule,
}) => {
  const startOfToday = startOfDay(serverDate)
  // Narrow-viewport check used only for the calendar row height. Cells
  // on phones should be short enough that 5-6 rows fit within the
  // body area (otherwise the user has to scroll just to see a month).
  const isNarrow =
    typeof window !== 'undefined' &&
    window.matchMedia('(max-width: 639px)').matches
  const rowMin = isNarrow ? 64 : 100
  return (
    <div className="h-full flex flex-col">
      <div className="grid grid-cols-7 border-b border-soft bg-surface">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div
            key={d}
            className="py-1.5 sm:py-2 text-center text-xs sm:text-sm font-medium text-muted border-r border-soft last:border-r-0"
          >
            {/* Single-letter labels on narrow screens to avoid clipping. */}
            <span className="sm:hidden">{d[0]}</span>
            <span className="hidden sm:inline">{d}</span>
          </div>
        ))}
      </div>
      <div
        className="grid grid-cols-7 flex-1"
        style={{ gridTemplateRows: `repeat(${Math.ceil(calendarDays.length / 7)}, minmax(${rowMin}px, 1fr))` }}
      >
        {calendarDays.map((day, i) => {
          const inMonth = isSameMonth(day, currentDate)
          const today = isSameDay(day, serverDate)
          const isPastDay = !today && day < startOfToday
          const dayEvents = getEventsForDay(day)
          const dateIso = format(day, 'yyyy-MM-dd')
          const dropHighlight = hoveredDayIso === dateIso

          return (
            <DayDropCell key={i} date={day}>
              {({ isOver, setNodeRef }) => (
                <div
                  ref={setNodeRef}
                  onClick={() => onDayClick(day)}
                  aria-label={isPastDay ? `${format(day, 'EEEE, MMMM d')} — past day` : undefined}
                  className={`relative border-b border-r border-soft p-1 cursor-pointer transition-colors ${
                    !inMonth ? 'bg-default/50' : isPastDay ? 'bg-default/40' : 'bg-surface'
                  } ${isPastDay && inMonth ? 'opacity-80' : ''} ${
                    isOver || dropHighlight ? 'ring-2 ring-brand-primary ring-inset bg-brand-subtle' : ''
                  } ${
                    isDragging && !isOver && inMonth ? 'hover:bg-brand-subtle/60' : 'hover:bg-brand-subtle'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div
                      className={`text-sm font-medium w-7 h-7 flex items-center justify-center rounded-full ${
                        today
                          ? 'bg-brand-primary text-on-brand'
                          : !inMonth
                            ? 'text-muted'
                            : isPastDay
                              ? 'text-muted'
                              : 'text-primary'
                      }`}
                    >
                      {format(day, 'd')}
                    </div>
                    {inMonth && <LocationLaneBadge count={locationCountForDay(day)} />}
                  </div>
                  <div className="space-y-0.5 overflow-hidden">
                    {dayEvents.slice(0, 3).map((ev) => {
                      const evIsPast = (ev.end ?? ev.start) < now
                      const itemIdNum = typeof ev.item_id === 'number' ? ev.item_id : null
                      const created = itemIdNum != null ? recentlyCreated[itemIdNum] : undefined
                      const inGrace = created != null && Date.now() - created < 12_000
                      return (
                        <EventPillCompact
                          key={ev.id}
                          event={ev}
                          isPast={evIsPast}
                          showCancel={inGrace && itemIdNum != null}
                          onUnschedule={() => itemIdNum != null && onUnschedule(itemIdNum, ev.title)}
                        />
                      )
                    })}
                    {dayEvents.length > 3 && (
                      <div className="text-xs text-muted px-2">
                        +{dayEvents.length - 3} more
                      </div>
                    )}
                  </div>
                  {isOver && (
                    <div className="absolute bottom-1 right-1 text-xs font-medium text-brand-primary bg-surface rounded px-1.5 py-0.5 shadow">
                      {isPastDay ? 'Drop to backdate' : 'Drop to schedule'}
                    </div>
                  )}
                </div>
              )}
            </DayDropCell>
          )
        })}
      </div>
    </div>
  )
}

interface EventPillCompactProps {
  event: ScheduleEvent
  isPast?: boolean
  showCancel?: boolean
  onUnschedule?: () => void
}

const EventPillCompact: React.FC<EventPillCompactProps> = ({
  event,
  isPast = false,
  showCancel = false,
  onUnschedule,
}) => (
  <div
    className="relative text-xs truncate rounded px-1.5 py-0.5 group"
    style={{
      backgroundColor:
        (event.color || EVENT_COLORS.default) + commitmentFillAlpha(event.commitment),
      color: event.color || EVENT_COLORS.default,
      // Dashed = Flexible: Horton's plan for this slot, still movable.
      borderLeft: `3px ${commitmentBorder(event.commitment)} ${
        event.color || EVENT_COLORS.default
      }`,
      opacity: isPast ? 0.6 : 1,
      filter: isPast ? 'saturate(0.75)' : undefined,
      paddingRight: showCancel ? '1.1rem' : undefined,
    }}
    title={
      event.commitment
        ? `${event.title} · ${COMMITMENT_HINT[event.commitment]}`
        : event.title
    }
  >
    {event.commitment && (
      <CommitmentGlyph state={event.commitment} className="inline w-2.5 h-2.5 mr-0.5 align-[-1px]" />
    )}
    {!event.allDay && (
      <span className="font-medium">{format(event.start, 'h:mma').toLowerCase()} </span>
    )}
    {event.title}
    {showCancel && onUnschedule && (
      <button
        type="button"
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()
          onUnschedule()
        }}
        aria-label={`Unschedule ${event.title}`}
        className="absolute top-0 right-0 px-1 py-0.5 text-bad hover:bg-bad/20 rounded"
      >
        <X className="w-3 h-3" />
      </button>
    )}
  </div>
)

interface WeekViewProps {
  weekDays: Date[]
  serverDate: Date
  now: Date
  getEventsForDay: (d: Date) => ScheduleEvent[]
  isDragging: boolean
  isEventDraggable: (e: ScheduleEvent) => boolean
  isEventEditable: (e: ScheduleEvent) => boolean
  locationTasks: OpportunisticLaneTask[]
  onOpenTaskFromPill: (taskId: number) => void
  onOpenEventEditor: (eventId: string) => void
  recentlyCreated: Record<number, number>
  onUnschedule: (itemId: number, title?: string) => void
}

const WeekView: React.FC<WeekViewProps> = ({
  weekDays,
  serverDate,
  now,
  getEventsForDay,
  isDragging,
  isEventDraggable,
  isEventEditable,
  locationTasks,
  onOpenTaskFromPill,
  onOpenEventEditor,
  recentlyCreated,
  onUnschedule,
}) => {
  const startOfToday = startOfDay(serverDate)
  // 48 half-hour rows per day × 7 days.
  const halfHourSteps = 48
  const halfHourHeight = WEEK_HOUR_HEIGHT / 2

  // Auto-scroll the time grid into the working-hours band (or the first
  // event, whichever is earlier). See pickScrollHour. We only scroll on
  // view-initial-paint for a given week AND again the first time events
  // load for that week, so the user's subsequent manual scrolling is
  // never clobbered.
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const hourGridRef = useRef<HTMLDivElement | null>(null)
  const weekFirstMs = weekDays[0]?.getTime() ?? 0
  const visibleEvents = useMemo(
    () => weekDays.flatMap((d) => getEventsForDay(d)),
    [weekDays, getEventsForDay],
  )
  const didScrollWithEventsRef = useRef<number>(0)
  useLayoutEffect(() => {
    if (!weekFirstMs) return
    const container = scrollContainerRef.current
    const grid = hourGridRef.current
    if (!container || !grid) return
    const hasEvents = visibleEvents.length > 0
    if (hasEvents && didScrollWithEventsRef.current === weekFirstMs) return
    const hour = pickScrollHour(visibleEvents)
    container.scrollTop = Math.max(
      0,
      grid.offsetTop + hour * WEEK_HOUR_HEIGHT - 40,
    )
    if (hasEvents) didScrollWithEventsRef.current = weekFirstMs
  }, [weekFirstMs, visibleEvents])

  // Keep all three stacked grids (date header, all-day row, hour grid) in
  // lockstep so their columns align. Each day column is at least 72px so
  // the week view remains readable on narrow viewports; the outer
  // scroller takes over horizontal overflow when the viewport is too
  // narrow to fit the full 7 + time-column template.
  const weekGridTemplate = '64px repeat(7, minmax(72px, 1fr))'
  return (
    <div ref={scrollContainerRef} className="h-full overflow-auto">
      <OpportunisticLane
        tasks={locationTasks}
        onOpenTask={onOpenTaskFromPill}
        label="Near you this week"
      />
      <div
        className="grid border-b border-soft bg-surface sticky top-0 z-10"
        style={{ gridTemplateColumns: weekGridTemplate }}
      >
        <div className="border-r border-soft" />
        {weekDays.map((day) => (
          <div
            key={day.toISOString()}
            className="py-2 text-center border-r border-soft last:border-r-0"
          >
            <div className="text-xs font-medium text-muted">{format(day, 'EEE')}</div>
            <div
              className={`text-base sm:text-xl font-semibold mt-1 w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full mx-auto ${
                isSameDay(day, serverDate) ? 'bg-brand-primary text-on-brand' : 'text-primary'
              }`}
            >
              {format(day, 'd')}
            </div>
          </div>
        ))}
      </div>
      <div
        className="grid border-b border-soft bg-default/50 min-h-10"
        style={{ gridTemplateColumns: weekGridTemplate }}
      >
        <div className="border-r border-soft px-2 py-1 text-xs text-muted truncate">
          All day
        </div>
        {weekDays.map((day) => (
          <div key={day.toISOString()} className="border-r border-soft last:border-r-0 p-1">
            {getEventsForDay(day)
              .filter((e) => e.allDay)
              .map((ev) => (
                <EventPillCompact key={ev.id} event={ev} />
              ))}
          </div>
        ))}
      </div>
      <div
        ref={hourGridRef}
        className="relative grid"
        style={{
          height: `${WEEK_HOUR_HEIGHT * 24}px`,
          gridTemplateColumns: weekGridTemplate,
        }}
      >
        {/* time labels column */}
        <div className="w-16 border-r border-soft relative">
          {HOURS.map((hour) => (
            <div
              key={hour}
              className="absolute w-full px-2 text-xs text-muted"
              style={{ top: `${hour * WEEK_HOUR_HEIGHT - 8}px` }}
            >
              {hour === 0 ? '' : format(setMinutes(setHours(new Date(), hour), 0), 'ha')}
            </div>
          ))}
          {HOURS.map((hour) => (
            <div
              key={`line-${hour}`}
              className="absolute w-full border-b border-soft"
              style={{ top: `${hour * WEEK_HOUR_HEIGHT}px`, height: `${WEEK_HOUR_HEIGHT}px` }}
            />
          ))}
        </div>
        {/* day columns */}
        {weekDays.map((day) => {
          const dayEvents = getEventsForDay(day).filter((e) => !e.allDay)
          // Precompute lane assignment + cascaded pixel boxes in one pass.
          // This handles both true overlap (side-by-side lanes) AND
          // back-to-back short pills that would otherwise visually
          // overlap because min-height (16px) inflates them past the
          // next pill's natural start.
          const boxMap = buildEventBoxes(
            dayEvents.map((ev) => {
              const sMin = ev.start.getHours() * 60 + ev.start.getMinutes()
              const eMin =
                ev.end != null
                  ? ev.end.getHours() * 60 + ev.end.getMinutes()
                  : sMin + 30
              return { id: ev.id, startMin: sMin, endMin: eMin }
            }),
            { pxPerHour: WEEK_HOUR_HEIGHT, minHeightPx: 16 },
          )
          const isToday = isSameDay(day, serverDate)
          const isPastDay = !isToday && day < startOfToday
          let pastPx = 0
          if (isPastDay) {
            pastPx = 24 * WEEK_HOUR_HEIGHT
          } else if (isToday) {
            pastPx = ((now.getHours() * 60 + now.getMinutes()) / 60) * WEEK_HOUR_HEIGHT
          }
          return (
            <div
              key={day.toISOString()}
              className="border-r border-soft last:border-r-0 relative"
            >
              {/* Hour grid lines for reference */}
              {HOURS.map((hour) => (
                <div
                  key={`ln-${hour}`}
                  className="absolute w-full border-b border-soft/70"
                  style={{
                    top: `${hour * WEEK_HOUR_HEIGHT}px`,
                    height: `${WEEK_HOUR_HEIGHT}px`,
                  }}
                />
              ))}
              {pastPx > 0 && (
                <PastTimeOverlay heightPx={pastPx} showNowLine={isToday} />
              )}
              {/* 30-min drop zones, only visible & interactive during drag */}
              {isDragging && (
                <div className="absolute inset-0">
                  {Array.from({ length: halfHourSteps }, (_, i) => {
                    const startMinute = i * 30
                    return (
                      <div
                        key={startMinute}
                        style={{
                          position: 'absolute',
                          top: `${i * halfHourHeight}px`,
                          left: 0,
                          right: 0,
                          height: `${halfHourHeight}px`,
                        }}
                      >
                        <TimeSlotDropZone
                          date={day}
                          startMinute={startMinute}
                          height={halfHourHeight}
                        />
                      </div>
                    )
                  })}
                </div>
              )}
              {/* Positioned events */}
              {dayEvents.map((ev) => {
                const startMin = ev.start.getHours() * 60 + ev.start.getMinutes()
                const endMin =
                  ev.end != null
                    ? ev.end.getHours() * 60 + ev.end.getMinutes()
                    : startMin + 30
                const box = boxMap.get(ev.id)
                const top = box?.top ?? (startMin / 60) * WEEK_HOUR_HEIGHT
                const height =
                  box?.height ??
                  Math.max(((endMin - startMin) / 60) * WEEK_HOUR_HEIGHT, 16)
                const draggable = isEventDraggable(ev)
                const itemIdNum = typeof ev.item_id === 'number' ? ev.item_id : null
                const evIsPast = (ev.end ?? ev.start) < now
                const editable = isEventEditable(ev)
                return (
                  <DraggableEventBlock
                    key={ev.id}
                    event={buildPillPayload(ev)}
                    top={top}
                    height={height}
                    draggable={draggable}
                    isPast={evIsPast}
                    onUnschedule={
                      itemIdNum != null
                        ? (id) => onUnschedule(id, ev.title)
                        : undefined
                    }
                    onOpen={editable ? onOpenEventEditor : undefined}
                    createdAtMs={itemIdNum != null ? recentlyCreated[itemIdNum] : undefined}
                    laneIndex={box?.laneIndex}
                    laneCount={box?.laneCount}
                  />
                )
              })}
            </div>
          )
        })}
      </div>
    </div>
  )
}

interface DayViewProps {
  currentDate: Date
  serverDate: Date
  now: Date
  getEventsForDay: (d: Date) => ScheduleEvent[]
  isDragging: boolean
  isEventDraggable: (e: ScheduleEvent) => boolean
  isEventEditable: (e: ScheduleEvent) => boolean
  locationTasks: OpportunisticLaneTask[]
  onOpenTaskFromPill: (taskId: number) => void
  onOpenEventEditor: (eventId: string) => void
  recentlyCreated: Record<number, number>
  onUnschedule: (itemId: number, title?: string) => void
}

const DayView: React.FC<DayViewProps> = ({
  currentDate,
  serverDate,
  now,
  getEventsForDay,
  isDragging,
  isEventDraggable,
  isEventEditable,
  locationTasks,
  onOpenTaskFromPill,
  onOpenEventEditor,
  recentlyCreated,
  onUnschedule,
}) => {
  const dayEvents = getEventsForDay(currentDate)
  const timed = dayEvents.filter((e) => !e.allDay)
  const allDay = dayEvents.filter((e) => e.allDay)
  // Compute lane assignment + cascaded pixel boxes (see week-view comment).
  const timedBoxMap = buildEventBoxes(
    timed.map((ev) => {
      const sMin = ev.start.getHours() * 60 + ev.start.getMinutes()
      const eMin =
        ev.end != null
          ? ev.end.getHours() * 60 + ev.end.getMinutes()
          : sMin + 30
      return { id: ev.id, startMin: sMin, endMin: eMin }
    }),
    { pxPerHour: HOUR_HEIGHT, minHeightPx: 24 },
  )
  const halfHourHeight = HOUR_HEIGHT / 2
  const halfHourSteps = 48
  const isToday = isSameDay(currentDate, serverDate)
  const startOfToday = startOfDay(serverDate)
  const isPastDay = !isToday && currentDate < startOfToday
  let pastPx = 0
  if (isPastDay) {
    pastPx = 24 * HOUR_HEIGHT
  } else if (isToday) {
    pastPx = ((now.getHours() * 60 + now.getMinutes()) / 60) * HOUR_HEIGHT
  }

  // Auto-scroll so the working-hours band (or the day's earliest event)
  // is visible on mount. See pickScrollHour. Mirrors WeekView's logic,
  // scoped to a single day.
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const hourBodyRef = useRef<HTMLDivElement | null>(null)
  const dayMs = startOfDay(currentDate).getTime()
  const didScrollWithEventsRef = useRef<number>(0)
  useLayoutEffect(() => {
    const container = scrollContainerRef.current
    const body = hourBodyRef.current
    if (!container || !body) return
    const hasEvents = timed.length > 0
    if (hasEvents && didScrollWithEventsRef.current === dayMs) return
    const hour = pickScrollHour(timed)
    container.scrollTop = Math.max(
      0,
      body.offsetTop + hour * HOUR_HEIGHT - 40,
    )
    if (hasEvents) didScrollWithEventsRef.current = dayMs
  }, [dayMs, timed])

  return (
    <div ref={scrollContainerRef} className="h-full overflow-auto">
      <OpportunisticLane
        tasks={locationTasks}
        onOpenTask={onOpenTaskFromPill}
        label="Near you"
      />
      <div className="border-b border-soft bg-surface sticky top-0 z-10 p-4">
        <div className="flex items-center gap-4">
          <div
            className={`text-4xl font-bold w-16 h-16 flex items-center justify-center rounded-full ${
              isSameDay(currentDate, serverDate)
                ? 'bg-brand-primary text-on-brand'
                : 'text-primary'
            }`}
          >
            {format(currentDate, 'd')}
          </div>
          <div>
            <div className="text-lg font-semibold text-primary">
              {format(currentDate, 'EEEE')}
            </div>
            <div className="text-sm text-muted">
              {format(currentDate, 'MMMM yyyy')}
            </div>
          </div>
        </div>
      </div>
      {allDay.length > 0 && (
        <div className="border-b border-soft bg-default/50 p-2">
          <div className="text-xs text-muted mb-2">All day</div>
          <div className="space-y-1">
            {allDay.map((ev) => (
              <EventPillCompact key={ev.id} event={ev} />
            ))}
          </div>
        </div>
      )}
      <div ref={hourBodyRef} className="flex">
        <div
          className="w-20 flex-shrink-0 border-r border-soft relative"
          style={{ height: `${HOUR_HEIGHT * 24}px` }}
        >
          {HOURS.map((hour) => (
            <div
              key={hour}
              className="absolute w-full px-2 text-xs text-muted flex items-center"
              style={{ top: `${hour * HOUR_HEIGHT - 8}px`, height: '16px' }}
            >
              {hour === 0 ? '' : format(setMinutes(setHours(new Date(), hour), 0), 'h:mm a')}
            </div>
          ))}
          {HOURS.map((hour) => (
            <div
              key={`line-${hour}`}
              className="absolute w-full border-b border-soft"
              style={{ top: `${hour * HOUR_HEIGHT}px`, height: `${HOUR_HEIGHT}px` }}
            />
          ))}
        </div>
        <div className="flex-1 relative" style={{ height: `${HOUR_HEIGHT * 24}px` }}>
          {/* Hour grid lines */}
          {HOURS.map((hour) => (
            <div
              key={hour}
              className="absolute w-full border-b border-soft"
              style={{ top: `${hour * HOUR_HEIGHT}px`, height: `${HOUR_HEIGHT}px` }}
            />
          ))}
          {pastPx > 0 && (
            <PastTimeOverlay heightPx={pastPx} showNowLine={isToday} />
          )}
          {/* 30-min drop zones during drag */}
          {isDragging && (
            <div className="absolute inset-0">
              {Array.from({ length: halfHourSteps }, (_, i) => {
                const startMinute = i * 30
                return (
                  <div
                    key={startMinute}
                    style={{
                      position: 'absolute',
                      top: `${i * halfHourHeight}px`,
                      left: 0,
                      right: 0,
                      height: `${halfHourHeight}px`,
                    }}
                  >
                    <TimeSlotDropZone
                      date={currentDate}
                      startMinute={startMinute}
                      height={halfHourHeight}
                    />
                  </div>
                )
              })}
            </div>
          )}
          {/* Positioned events */}
          {timed.map((ev) => {
            const startMin = ev.start.getHours() * 60 + ev.start.getMinutes()
            const endMin =
              ev.end != null
                ? ev.end.getHours() * 60 + ev.end.getMinutes()
                : startMin + 30
            const box = timedBoxMap.get(ev.id)
            const top = box?.top ?? (startMin / 60) * HOUR_HEIGHT
            const height =
              box?.height ?? Math.max(((endMin - startMin) / 60) * HOUR_HEIGHT, 24)
            const draggable = isEventDraggable(ev)
            const itemIdNum = typeof ev.item_id === 'number' ? ev.item_id : null
            const evIsPast = (ev.end ?? ev.start) < now
            const editable = isEventEditable(ev)
            return (
              <DraggableEventBlock
                key={ev.id}
                event={buildPillPayload(ev)}
                top={top}
                height={height}
                draggable={draggable}
                isPast={evIsPast}
                onUnschedule={
                  itemIdNum != null
                    ? (id) => onUnschedule(id, ev.title)
                    : undefined
                }
                onOpen={editable ? onOpenEventEditor : undefined}
                createdAtMs={itemIdNum != null ? recentlyCreated[itemIdNum] : undefined}
                laneIndex={box?.laneIndex}
                laneCount={box?.laneCount}
              />
            )
          })}
        </div>
      </div>
    </div>
  )
}

interface AgendaViewProps {
  weekDays: Date[]
  serverDate: Date
  now: Date
  getEventsForDay: (d: Date) => ScheduleEvent[]
}

const AgendaView: React.FC<AgendaViewProps> = ({ weekDays, serverDate, now, getEventsForDay }) => {
  const anyEvents = weekDays.some((d) => getEventsForDay(d).length > 0)
  return (
    <div className="h-full overflow-auto p-3 sm:p-6">
      <div className="max-w-3xl mx-auto">
        {!anyEvents && (
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 text-muted mx-auto mb-4" />
            <h4 className="text-lg font-semibold text-secondary mb-2">
              No events this week
            </h4>
            <p className="text-muted">
              Drag tasks from the sidebar to schedule them.
            </p>
          </div>
        )}
        {weekDays.map((day) => {
          const dayEvents = getEventsForDay(day)
          if (dayEvents.length === 0) return null
          return (
            <DayDropCell key={day.toISOString()} date={day}>
              {({ isOver, setNodeRef }) => (
                <div
                  ref={setNodeRef}
                  className={`mb-6 rounded-lg transition-colors ${
                    isOver ? 'ring-2 ring-brand-primary bg-brand-subtle p-3' : ''
                  }`}
                >
                  <div className="flex items-center gap-4 mb-3">
                    <div
                      className={`text-xl font-semibold w-12 h-12 flex items-center justify-center rounded-full ${
                        isSameDay(day, serverDate)
                          ? 'bg-brand-primary text-on-brand'
                          : 'bg-surface-alt text-primary'
                      }`}
                    >
                      {format(day, 'd')}
                    </div>
                    <div>
                      <div className="font-semibold text-primary">
                        {format(day, 'EEEE')}
                      </div>
                      <div className="text-sm text-muted">
                        {format(day, 'MMMM yyyy')}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2 ml-16">
                    {dayEvents.map((event) => {
                      const evIsPast = (event.end ?? event.start) < now
                      return (
                      <div
                        key={event.id}
                        className="p-4 rounded-lg border border-soft bg-surface hover:shadow-md transition-all"
                        style={{
                          borderLeftColor: event.color,
                          borderLeftWidth: '4px',
                          borderLeftStyle: commitmentBorder(event.commitment),
                          opacity: evIsPast ? 0.7 : 1,
                          filter: evIsPast ? 'saturate(0.8)' : undefined,
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-semibold text-primary">
                              {event.title}
                            </h4>
                            {event.description && (
                              <p className="text-sm text-muted mt-1">
                                {event.description}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <CommitmentChip state={event.commitment} />
                            <span
                              className="px-2 py-1 rounded text-xs font-medium"
                              style={{
                                backgroundColor: event.color + '20',
                                color: event.color,
                              }}
                            >
                              {event.type.replace('_', ' ')}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 mt-3 text-sm text-muted">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {event.allDay ? 'All day' : format(event.start, 'h:mm a')}
                          </span>
                          {evIsPast && (
                            <span className="text-xs text-muted italic">past</span>
                          )}
                        </div>
                      </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </DayDropCell>
          )
        })}
      </div>
    </div>
  )
}

export default ScheduleV2
